import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

/**
 * Export data to styled Excel file with colored headers and professional formatting
 * @param {Array} data - Array of data objects
 * @param {Array} headers - Array of header objects with label and key
 * @param {String} filename - Name of the file to download
 * @param {Object} options - Styling options
 */
export const exportToStyledExcel = async (data, headers, filename, options = {}) => {
    const {
        headerColor = '4472C4', // Default blue color
        headerFontColor = 'FFFFFF', // White text
        sheetName = 'Sheet1',
        title = null,
    } = options;

    // Create workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName, {
        views: [{ state: 'frozen', ySplit: 1 }] // Freeze header row
    });
    
    // Add title if provided
    let startRow = 1;
    if (title) {
        worksheet.mergeCells('A1', String.fromCharCode(64 + headers.length) + '1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = title;
        titleCell.font = { size: 16, bold: true, color: { argb: 'FF' + headerColor } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        worksheet.getRow(1).height = 30;
        startRow = 2;
    }
    
    // Add headers
    const headerRow = worksheet.getRow(startRow);
    headers.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header.label;
        cell.font = { 
            bold: true, 
            color: { argb: 'FF' + headerFontColor },
            size: 11
        };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF' + headerColor }
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };
    });
    headerRow.height = 25;
    
    // Add data rows
    data.forEach((item) => {
        const row = worksheet.addRow(headers.map(h => item[h.key] || ''));
        row.eachCell((cell) => {
            cell.border = {
                top: { style: 'thin', color: { argb: 'FFD3D3D3' } },
                left: { style: 'thin', color: { argb: 'FFD3D3D3' } },
                bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } },
                right: { style: 'thin', color: { argb: 'FFD3D3D3' } }
            };
            cell.alignment = { vertical: 'middle' };
        });
    });
    
    // Auto-fit columns
    worksheet.columns.forEach((column, index) => {
        let maxLength = headers[index]?.label?.length || 10;
        data.forEach(row => {
            const value = String(row[headers[index]?.key] || '');
            if (value.length > maxLength) {
                maxLength = value.length;
            }
        });
        column.width = Math.min(Math.max(maxLength + 3, 12), 50);
    });
    
    // Add auto-filter
    worksheet.autoFilter = {
        from: { row: startRow, column: 1 },
        to: { row: startRow, column: headers.length }
    };
    
    // Generate and download file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    saveAs(blob, `${filename}.xlsx`);
};

/**
 * Export button component for styled Excel
 */
export const StyledExcelButton = ({ 
    data, 
    headers, 
    filename, 
    buttonText = 'Download Excel',
    className = '',
    options = {},
    disabled = false
}) => {
    const handleDownload = async () => {
        if (!data || data.length === 0) {
            alert('No data available to download');
            return;
        }
        try {
            await exportToStyledExcel(data, headers, filename, options);
        } catch (error) {
            console.error('Error downloading Excel:', error);
            alert('Failed to download Excel file. Please try again.');
        }
    };
    
    return (
        <button
            onClick={handleDownload}
            disabled={disabled || !data || data.length === 0}
            className={className}
            style={{
                padding: '8px 16px',
                backgroundColor: disabled || !data || data.length === 0 ? '#d9d9d9' : '#1890ff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: disabled || !data || data.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s',
                ...options.buttonStyle
            }}
            onMouseEnter={(e) => {
                if (!disabled && data && data.length > 0) {
                    e.target.style.backgroundColor = '#096dd9';
                }
            }}
            onMouseLeave={(e) => {
                if (!disabled && data && data.length > 0) {
                    e.target.style.backgroundColor = '#1890ff';
                }
            }}
        >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {buttonText}
        </button>
    );
};

